import React, { useState, useMemo } from 'react';
import Carousel from '../components/Carousel';
import BarbershopList from '../components/BarbershopList';
import SponsorCarousel from '../components/SponsorCarousel';
import { LoadingGrid } from '../components/LoadingComponents';
import { useAppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { icons } from '../components/ui/Icons';

/**
 * Página: Home (/pages/HomePage.jsx)
 * Página inicial da aplicação
 */
const HomePage = () => {
  const { barbershops, loading } = useAppContext();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [minRating, setMinRating] = useState(0);
  const [selectedService, setSelectedService] = useState('all');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 500 });
  const [showFilters, setShowFilters] = useState(false);

  const onNavigate = (targetPage, params = {}) => {
    if (targetPage === 'barbershop' && params.id) {
      navigate(`/barbershop/${params.id}`);
    }
  };

  // Extrair cidades únicas
  const cities = useMemo(() => {
    const uniqueCities = [...new Set(barbershops.map(b => b.city))].sort();
    return uniqueCities;
  }, [barbershops]);

  // Extrair serviços únicos de todas as barbearias
  const services = useMemo(() => {
    const allServices = new Set();
    barbershops.forEach(shop => {
      if (shop.services && Array.isArray(shop.services)) {
        shop.services.forEach(service => {
          if (typeof service === 'string') {
            allServices.add(service);
          } else if (service.name) {
            allServices.add(service.name);
          }
        });
      }
    });
    return Array.from(allServices).sort();
  }, [barbershops]);

  // Filtrar barbearias
  const filteredShops = useMemo(() => {
    return barbershops.filter(shop => {
      // Filtro de busca por nome ou cidade
      const matchesSearch = shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           shop.city.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtro por cidade
      const matchesCity = selectedCity === 'all' || shop.city === selectedCity;
      
      // Filtro por avaliação
      const matchesRating = shop.rating >= minRating;
      
      // Filtro por serviço
      let matchesService = selectedService === 'all';
      if (!matchesService && shop.services && Array.isArray(shop.services)) {
        matchesService = shop.services.some(service => {
          const serviceName = typeof service === 'string' ? service : service.name;
          return serviceName === selectedService;
        });
      }
      
      // Filtro por faixa de preço
      let matchesPrice = true;
      if (shop.services && Array.isArray(shop.services)) {
        const prices = shop.services.map(service => {
          if (typeof service === 'object' && service.price) {
            return Number(service.price);
          }
          return 0;
        }).filter(p => p > 0);
        
        if (prices.length > 0) {
          const minPrice = Math.min(...prices);
          matchesPrice = minPrice >= priceRange.min && minPrice <= priceRange.max;
        }
      }
      
      return matchesSearch && matchesCity && matchesRating && matchesService && matchesPrice;
    });
  }, [barbershops, searchTerm, selectedCity, minRating, selectedService, priceRange]);

  const sponsoredShops = useMemo(() =>
    filteredShops
      .filter((s) => s.isSponsored)
      .sort((a, b) => b.rating - a.rating),
    [filteredShops]
  );

  const nonSponsoredShops = useMemo(() =>
    filteredShops
      .filter((s) => !s.isSponsored)
      .sort((a, b) => b.rating - a.rating),
    [filteredShops]
  );

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCity('all');
    setMinRating(0);
    setSelectedService('all');
    setPriceRange({ min: 0, max: 500 });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main>
        {/* Seção do Carrossel */}
        <div className="py-8 md:py-12 bg-white">
          <Carousel items={sponsoredShops} onNavigate={onNavigate} />
        </div>

        {/* Banner de Patrocínio */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <SponsorCarousel count={3} interval={7000} className="w-full" />
        </div>

        {/* Conteúdo Principal (Listas) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          
          {/* Barra de Busca e Filtros */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              
              {/* Campo de Busca */}
              <div className="flex-1">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar barbearia
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="search"
                    placeholder="Digite o nome ou cidade..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-3 pl-10 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔍
                  </div>
                </div>
              </div>

              {/* Botão Toggle Filtros Avançados */}
              <div className="flex items-end">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-4 py-3 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors font-medium flex items-center gap-2"
                >
                  {icons.settings({ className: 'h-5 w-5' })}
                  Filtros Avançados
                  <span className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
              </div>
            </div>

            {/* Filtros Avançados (Expansível) */}
            {showFilters && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  
                  {/* Filtro por Cidade */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cidade
                    </label>
                    <select
                      value={selectedCity}
                      onChange={(e) => setSelectedCity(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="all">Todas as cidades</option>
                      {cities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por Serviço */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Serviço
                    </label>
                    <select
                      value={selectedService}
                      onChange={(e) => setSelectedService(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="all">Todos os serviços</option>
                      {services.map(service => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por Avaliação Mínima */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Avaliação mínima
                    </label>
                    <select
                      value={minRating}
                      onChange={(e) => setMinRating(Number(e.target.value))}
                      className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value={0}>Todas</option>
                      <option value={3}>3+ estrelas</option>
                      <option value={4}>4+ estrelas</option>
                      <option value={4.5}>4.5+ estrelas</option>
                    </select>
                  </div>

                  {/* Filtro por Faixa de Preço */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Faixa de Preço
                    </label>
                    <select
                      value={`${priceRange.min}-${priceRange.max}`}
                      onChange={(e) => {
                        const [min, max] = e.target.value.split('-').map(Number);
                        setPriceRange({ min, max });
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="0-500">Todos os preços</option>
                      <option value="0-30">Até R$ 30</option>
                      <option value="30-50">R$ 30 - R$ 50</option>
                      <option value="50-80">R$ 50 - R$ 80</option>
                      <option value="80-500">Acima de R$ 80</option>
                    </select>
                  </div>
                </div>

                {/* Botão Limpar Filtros */}
                <div className="mt-4">
                  <button
                    onClick={clearFilters}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Limpar Todos os Filtros
                  </button>
                </div>

                {/* Contador de Resultados */}
                <div className="mt-4 text-sm text-gray-600">
                  {filteredShops.length === 0 ? (
                    <span className="text-red-600">Nenhuma barbearia encontrada com esses filtros</span>
                  ) : (
                    <span>
                      Exibindo <span className="font-semibold">{filteredShops.length}</span> barbearia(s)
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Listas */}
          {loading ? (
            <LoadingGrid count={6} columns={3} />
          ) : (
            <>
              {sponsoredShops.length > 0 && (
                <BarbershopList
                  title="Patrocinadas"
                  shops={sponsoredShops}
                  onNavigate={onNavigate}
                />
              )}
              {nonSponsoredShops.length > 0 && (
                <BarbershopList
                  title="Outras Barbearias"
                  shops={nonSponsoredShops}
                  onNavigate={onNavigate}
                />
              )}
              
              {/* Empty State */}
              {filteredShops.length === 0 && (
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">😔</div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Nenhuma barbearia encontrada
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Tente ajustar os filtros ou buscar por outro termo
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Limpar Filtros
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default HomePage;
